import { lazy, Suspense, type ReactNode } from 'react';

/**
 * Ingest credentials are read once at module load. `undefined` is the normal
 * case for local dev, CI and E2E — see `VITE_TRACEWAY_CONNECTION_STRING`.
 */
const connectionString = import.meta.env.VITE_TRACEWAY_CONNECTION_STRING;

/**
 * Imported only when configured: the SDK bundles rrweb (~0.5 MB), which every
 * player would otherwise download and parse for nothing.
 */
const TracewayProvider = lazy(() => import('@tracewayapp/react').then((m) => ({ default: m.TracewayProvider })));

/**
 * Mounts the Traceway client only when a connection string is configured,
 * otherwise renders children untouched. Keeping the decision here (rather than
 * an `import.meta.env.PROD` check) is what lets us point a local `npm run dev`
 * at the self-hosted instance to verify session replay end to end.
 *
 * `TracewayProvider` doubles as an error boundary since SDK v1.1, but it has no
 * `fallback` prop — it rethrows after reporting, so the existing outer boundary
 * still renders the crash screen. That ordering matters: Traceway must sit
 * *inside* the boundary that renders UI, or the user sees a blank page.
 */
export function TracewayGate({ children }: { children: ReactNode }) {
  if (!connectionString) return <>{children}</>;

  return (
    <Suspense fallback={null}>
    <TracewayProvider
      connectionString={connectionString}
      options={{
        version: __TRACEWAY_RELEASE__,
        // Replay ships with the exception that triggered it (~30s of DOM
        // events). Recording every session instead would mean a continuous
        // upload stream, which this Raspberry Pi does not have the headroom for.
        sessionRecording: true,
        // 5xx responses already surface as backend issues; capturing them here
        // too would double-report every outage.
        captureHttpServerErrors: false,
        ignoreErrors: [
          // The SDK's own rrweb recorder throws this while re-syncing its DOM
          // snapshot. Verified from the reported stack: every frame is inside
          // @tracewayapp_react.js (takeFullSnapshot -> emit -> processMutations),
          // no application code involved, and the replay still plays back fine.
          // Left unfiltered it files itself as an issue on every page load.
          /please take full snapshot after start recording/,
        ],
      }}
    >
      {children}
    </TracewayProvider>
    </Suspense>
  );
}
